<script lang="ts">
  import { ArrowRight } from 'lucide-svelte';
  import { Motion } from "svelte-motion";
  import { onMount } from 'svelte';

  interface Path {
    id: number;
    d: string;
    color: string;
    width: number;
  }

  function generatePaths(position: number): Path[] {
    return Array.from({ length: 36 }, (_, i) => ({
      id: i,
      d: `M-${380 - i * 5 * position} -${189 + i * 6}C-${380 - i * 5 * position} -${189 + i * 6} -${312 - i * 5 * position} ${216 - i * 6} ${152 - i * 5 * position} ${343 - i * 6}C${616 - i * 5 * position} ${470 - i * 6} ${684 - i * 5 * position} ${875 - i * 6} ${684 - i * 5 * position} ${875 - i * 6}`,
      color: `rgba(15,23,42,${0.1 + i * 0.03})`,
      width: 0.5 + i * 0.03,
    }));
  }

  const paths1 = generatePaths(1);
  const paths2 = generatePaths(-1);

  // Disable animations on mobile
  let enableAnimations = true;
  onMount(() => {
    enableAnimations = window.innerWidth > 768;
  });
</script>

<section class="pt-32 md:pt-40 pb-20 md:pb-32 px-4 md:px-8 relative overflow-hidden min-h-screen flex items-center">
  <!-- Background Paths -->
  <div class="absolute inset-0 hidden md:block">
    {#if enableAnimations}
      <!-- First set of floating paths -->
      <div class="absolute inset-0 pointer-events-none">
        <svg class="w-full h-full text-white opacity-30" viewBox="0 0 696 316" fill="none">
          <title>Background Paths</title>
          {#each paths1 as path (path.id)}
            <Motion
              let:motion
              initial={{ pathLength: 0.3, opacity: 0.6 }}
              animate={{
                pathLength: 1,
                opacity: [0.3, 0.6, 0.3],
                pathOffset: [0, 1, 0]
              }}
              transition={{
                duration: 20 + Math.random() * 10,
                repeat: Infinity,
                ease: "linear"
              }}
            >
              <path
                use:motion
                d={path.d}
                stroke="currentColor"
                stroke-width={path.width}
                stroke-opacity={0.2 + path.id * 0.04}
              />
            </Motion>
          {/each}
        </svg>
      </div>

      <!-- Second set of floating paths -->
      <div class="absolute inset-0 pointer-events-none">
        <svg class="w-full h-full text-white opacity-30" viewBox="0 0 696 316" fill="none">
          <title>Background Paths</title>
          {#each paths2 as path (path.id)}
            <Motion
              let:motion
              initial={{ pathLength: 0.3, opacity: 0.6 }}
              animate={{
                pathLength: 1,
                opacity: [0.3, 0.6, 0.3],
                pathOffset: [0, 1, 0]
              }}
              transition={{
                duration: 20 + Math.random() * 10,
                repeat: Infinity,
                ease: "linear"
              }}
            >
              <path
                use:motion
                d={path.d}
                stroke="currentColor"
                stroke-width={path.width}
                stroke-opacity={0.2 + path.id * 0.04}
              />
            </Motion>
          {/each}
        </svg>
      </div>
    {/if}
  </div>

  <div class="max-w-5xl mx-auto w-full relative z-10">
    <div class="relative">
      <div class="relative z-10 text-center">
        <div class="inline-flex items-center gap-2 bg-white/5 px-4 py-1.5 rounded-full mb-8 border border-white/10 backdrop-blur-sm">
          <span class="text-slate-400 text-xs tracking-wide uppercase">
            Start vibecoding today
          </span>
        </div>

        <h1 class="text-white mb-6 md:mb-8 text-5xl sm:text-6xl md:text-7xl lg:text-8xl max-w-4xl mx-auto leading-tight">
          BuyKeys.store
        </h1>

        <p class="text-slate-400 text-base md:text-lg lg:text-xl mb-10 md:mb-12 max-w-2xl mx-auto leading-relaxed px-4">
          We offer the highest quality API keys with
          warranty in case anything goes wrong. Start
          vibecoding today at a fraction of the real cost.
        </p>

        <div class="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center mb-16 md:mb-24 px-4">
          <a
            href="#pricing"
            class="w-full sm:w-auto px-6 md:px-8 py-3 md:py-3.5 bg-white text-black rounded-md transition-all hover:-translate-y-0.5 group flex items-center justify-center gap-2 text-sm md:text-base"
            style="box-shadow: 0 4px 14px 0 rgba(255, 255, 255, 0.2), 0 8px 24px 0 rgba(255, 255, 255, 0.12)"
          >
            Get Started Now
            <ArrowRight class="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
          <a href="https://t.me/BuyKeysSupport" target="_blank" rel="noopener noreferrer" class="w-full sm:w-auto px-6 md:px-8 py-3 md:py-3.5 text-white rounded-md transition-all hover:bg-white/5 border border-white/20 text-sm md:text-base inline-block text-center">
            Contact Us
          </a>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-3 gap-8 md:gap-12 max-w-3xl mx-auto px-4">
          <div class="text-center">
            <div
              class="text-4xl md:text-5xl text-white mb-2 md:mb-3 font-light"
              style="font-family: var(--font-serif)"
            >
              99.9%
            </div>
            <div class="text-slate-500 text-xs md:text-sm uppercase tracking-wider">
              Reliability
            </div>
          </div>
          <div class="text-center">
            <div
              class="text-4xl md:text-5xl text-white mb-2 md:mb-3 font-light"
              style="font-family: var(--font-serif)"
            >
              500,000+
            </div>
            <div class="text-slate-500 text-xs md:text-sm uppercase tracking-wider">
              Requests Sent
            </div>
          </div>
          <div class="text-center">
            <div
              class="text-4xl md:text-5xl text-white mb-2 md:mb-3 font-light"
              style="font-family: var(--font-serif)"
            >
              24/7
            </div>
            <div class="text-slate-500 text-xs md:text-sm uppercase tracking-wider">
              Support
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>
